import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Navigate } from 'react-router-dom'

import { ApiClientError } from '@/api/client'
import {
  createAdminVendor,
  fetchAdminVendors,
  sendAdminVendorPasswordReset,
  setAdminVendorActive,
  updateAdminVendor,
  type PasswordResetResult,
  type VendorAccount,
  type VendorUpsertPayload,
} from '@/api/vendorsApi'
import { useAuth } from '@/auth/AuthContext'
import { AdminHeaderActions } from '@/components/admin/AdminHeaderActions'

import authStyles from './AuthPages.module.css'
import styles from './PortalPages.module.css'

const EMPTY_FORM: VendorUpsertPayload = {
  username: '',
  email: '',
  full_name: '',
  whatsapp_number: '',
  business_name: '',
  is_active: false,
}

function toForm(vendor: VendorAccount): VendorUpsertPayload {
  return {
    username: vendor.username,
    email: vendor.email,
    full_name: vendor.full_name,
    whatsapp_number: vendor.whatsapp_number || '',
    business_name: vendor.business_name || '',
    is_active: vendor.is_active,
  }
}

/** Admin page to create, edit, activate, and reset vendor accounts. */
export function AdminVendorsPage() {
  const { isAuthenticated, isAdmin, logout } = useAuth()
  const [vendors, setVendors] = useState<VendorAccount[]>([])
  const [form, setForm] = useState<VendorUpsertPayload>(EMPTY_FORM)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState('')
  const [busy, setBusy] = useState(false)
  const [showInactive, setShowInactive] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [resetLink, setResetLink] = useState<PasswordResetResult | null>(null)

  async function reload() {
    const result = await fetchAdminVendors()
    setVendors(result.vendors)
  }

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) return
    void (async () => {
      try {
        await reload()
      } catch (err) {
        setError(err instanceof ApiClientError ? err.message : 'Could not load vendors.')
      }
    })()
  }, [isAuthenticated, isAdmin])

  const { active, inactive } = useMemo(() => {
    const activeVendors = vendors.filter((vendor) => vendor.is_active)
    const inactiveVendors = vendors.filter((vendor) => !vendor.is_active)
    return { active: activeVendors, inactive: inactiveVendors }
  }, [vendors])

  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (!isAdmin) return <Navigate to="/vendor" replace />

  function updateField<K extends keyof VendorUpsertPayload>(key: K, value: VendorUpsertPayload[K]) {
    setForm((current) => ({ ...current, [key]: value }))
    setSaved('')
  }

  function resetForm() {
    setEditingId(null)
    setForm(EMPTY_FORM)
    setShowForm(false)
    setSaved('')
  }

  function startCreate() {
    setEditingId(null)
    setForm(EMPTY_FORM)
    setShowForm(true)
    setError('')
    setSaved('')
    setResetLink(null)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  function startEdit(vendor: VendorAccount) {
    setEditingId(vendor.id)
    setForm(toForm(vendor))
    setShowForm(true)
    setError('')
    setSaved('')
    setResetLink(null)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    setSaved('')
    setResetLink(null)
    try {
      if (editingId == null) {
        await createAdminVendor(form)
        setSaved(
          'Vendor created as inactive (cannot sign in yet). Send a password reset so they can set a password and activate.',
        )
        setShowInactive(true)
      } else {
        await updateAdminVendor(editingId, {
          username: form.username,
          email: form.email,
          full_name: form.full_name,
          whatsapp_number: form.whatsapp_number,
          business_name: form.business_name,
          is_active: form.is_active,
        })
        setSaved('Vendor account updated.')
      }
      await reload()
      resetForm()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not save vendor.')
    } finally {
      setBusy(false)
    }
  }

  async function onToggleActive(vendor: VendorAccount) {
    setBusy(true)
    setError('')
    try {
      await setAdminVendorActive(vendor.id, !vendor.is_active)
      await reload()
      if (editingId === vendor.id) {
        setForm((current) => ({ ...current, is_active: !vendor.is_active }))
      }
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not update vendor status.')
    } finally {
      setBusy(false)
    }
  }

  async function onSendPasswordReset(vendor: VendorAccount) {
    setBusy(true)
    setError('')
    setSaved('')
    setResetLink(null)
    try {
      const result = await sendAdminVendorPasswordReset(vendor.id)
      if (result.emailed) {
        setSaved(`Password reset email sent to ${result.email}. Link expires in ${result.expires_hours}h.`)
      } else if (result.reset_url) {
        setResetLink(result)
        setSaved(
          `Email is not configured — copy the reset link below and send it to ${result.email} (expires in ${result.expires_hours}h).`,
        )
      } else {
        setSaved(`Password reset prepared for ${result.email}.`)
      }
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not send password reset.')
    } finally {
      setBusy(false)
    }
  }

  async function copyResetUrl() {
    if (!resetLink?.reset_url) return
    try {
      await navigator.clipboard.writeText(resetLink.reset_url)
      setSaved('Reset link copied to clipboard.')
    } catch {
      setError('Could not copy — select the link and copy it manually.')
    }
  }

  function renderVendorList(items: VendorAccount[]) {
    if (items.length === 0) {
      return <p className={styles.empty}>No vendors in this list.</p>
    }
    return items.map((vendor) => (
      <article key={vendor.id} className={styles.item}>
        <div>
          <h2 className={styles.itemTitle}>
            {vendor.full_name}
            {vendor.business_name ? ` · ${vendor.business_name}` : ''}
          </h2>
          <p className={styles.meta}>
            @{vendor.username} · {vendor.email}
            {vendor.whatsapp_number ? ` · WA ${vendor.whatsapp_number}` : ''}
          </p>
          <p className={styles.meta}>
            Status:{' '}
            <strong className={vendor.is_active ? styles.metaOk : styles.metaAlert}>
              {vendor.is_active ? 'active' : 'inactive'}
            </strong>
            {vendor.created_at ? ` · joined ${vendor.created_at.slice(0, 10)}` : ''}
          </p>
        </div>
        <div className={styles.actionCol}>
          <button type="button" className={styles.secondary} disabled={busy} onClick={() => startEdit(vendor)}>
            Edit
          </button>
          <button
            type="button"
            className={styles.primary}
            disabled={busy}
            onClick={() => void onSendPasswordReset(vendor)}
          >
            Send password reset
          </button>
          <button
            type="button"
            className={vendor.is_active ? styles.danger : styles.ghost}
            disabled={busy}
            onClick={() => void onToggleActive(vendor)}
          >
            {vendor.is_active ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      </article>
    ))
  }

  return (
    <section className={styles.page}>
      <header className={styles.header}>
        <div>
          <p className={styles.brand}>Oikos Market Day</p>
          <h1 className={styles.title}>Admin · Vendors</h1>
          <p className={styles.lead}>
            Create paper-application vendors (inactive until they set a password), send password resets, and
            activate or deactivate logins.
          </p>
        </div>
        <AdminHeaderActions logout={logout} />
      </header>

      {error ? <p className={styles.error}>{error}</p> : null}
      {saved ? <p className={styles.metaOk}>{saved}</p> : null}
      {resetLink?.reset_url ? (
        <div className={styles.item} style={{ marginBottom: '1rem' }}>
          <p className={styles.meta}>
            Reset link for <strong>{resetLink.email}</strong>:
          </p>
          <p className={styles.meta} style={{ wordBreak: 'break-all' }}>
            {resetLink.reset_url}
          </p>
          <div className={styles.actions}>
            <button type="button" className={styles.primary} onClick={() => void copyResetUrl()}>
              Copy link
            </button>
            <button type="button" className={styles.ghost} onClick={() => setResetLink(null)}>
              Dismiss
            </button>
          </div>
        </div>
      ) : null}

      {showForm ? (
        <form className={styles.formGrid} onSubmit={(event) => void onSubmit(event)}>
          <h2 className={styles.itemTitle}>
            {editingId != null ? `Edit vendor #${editingId}` : 'New vendor account'}
          </h2>
          {editingId == null ? (
            <p className={styles.meta}>
              No password is set here. The account stays inactive until you send a password reset and the
              vendor chooses a password.
            </p>
          ) : null}

          <label className={authStyles.label}>
            Full name
            <input
              className={authStyles.input}
              value={form.full_name}
              onChange={(e) => updateField('full_name', e.target.value)}
              required
            />
          </label>
          <label className={authStyles.label}>
            Business name
            <input
              className={authStyles.input}
              value={form.business_name || ''}
              onChange={(e) => updateField('business_name', e.target.value)}
            />
          </label>
          <label className={authStyles.label}>
            Username
            <input
              className={authStyles.input}
              value={form.username}
              onChange={(e) => updateField('username', e.target.value)}
              required
            />
          </label>
          <label className={authStyles.label}>
            Email
            <input
              className={authStyles.input}
              type="email"
              value={form.email}
              onChange={(e) => updateField('email', e.target.value)}
              required
            />
          </label>
          <label className={authStyles.label}>
            WhatsApp number
            <input
              className={authStyles.input}
              type="tel"
              value={form.whatsapp_number}
              onChange={(e) => updateField('whatsapp_number', e.target.value)}
              placeholder="e.g. 082 123 4567"
              required
            />
          </label>
          {editingId != null ? (
            <label className={styles.check}>
              <input
                type="checkbox"
                checked={Boolean(form.is_active)}
                onChange={(e) => updateField('is_active', e.target.checked)}
              />
              Account is active (can sign in)
            </label>
          ) : null}

          <div className={styles.actions}>
            <button className={authStyles.submit} type="submit" disabled={busy}>
              {busy ? 'Saving…' : editingId != null ? 'Save vendor' : 'Create vendor'}
            </button>
            <button type="button" className={styles.ghost} onClick={resetForm} disabled={busy}>
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      <div className={styles.list} style={{ marginTop: showForm ? '1.5rem' : 0 }}>
        <div className={styles.header} style={{ marginBottom: '0.75rem' }}>
          <h2 className={styles.itemTitle}>Active vendors ({active.length})</h2>
          <div className={styles.actions}>
            <button type="button" className={styles.primary} onClick={startCreate}>
              New vendor
            </button>
            <button
              type="button"
              className={styles.secondary}
              onClick={() => setShowInactive((current) => !current)}
            >
              {showInactive ? 'Hide inactive' : `List inactive (${inactive.length})`}
            </button>
          </div>
        </div>
        {renderVendorList(active)}

        {showInactive ? (
          <>
            <h2 className={styles.itemTitle} style={{ marginTop: '1.5rem' }}>
              Inactive vendors
            </h2>
            {renderVendorList(inactive)}
          </>
        ) : null}
      </div>
    </section>
  )
}
